<!--
  @fileoverview Message Buttons Component - Action Button Renderer

  Renders action buttons within chat message bubbles. Buttons can be:
  - Postback buttons: Send a payload back to the bot
  - URL buttons: Open a link in a new tab/window
  - Upload buttons: Trigger file upload dialog
  - Popup buttons: Open URL in a sized popup window

  @component MessageButtons
  @displayName Action Buttons

  Features:
  - Postback buttons for bot interactions
  - URL buttons with configurable target (_blank, popup)
  - Popup window support with custom dimensions
  - Upload trigger detection
  - One-time use button support (disabled after click)
  - Live chat disable mode

  @example
  <MessageButtons
    :message="messageWithButtons"
    :button-outline="'primary'"
    :should-disable-if-in-livechat="false"
    @sendPostback="handleButtonClick"
    @upload="handleUpload"
  />

  @vue3migration COMPLETED
  - Converted to Vue 3 Composition API with script setup
  - Replaced this.$store.getters.settings with Pinia useMainStore().settings
  - Replaced this.$root.$emit("upload") with emit("upload") - parent handles routing
  - Replaced this.$emit() with emit() from defineEmits()
  - Removed unused lodash-es import
  - Added proper TypeScript-style prop definitions
-->
<template>
  <!-- Vertical stack of buttons -->
  <div class="flex flex-col">
    <div
      v-for="(button, buttonIndex) in message.data.content[0].buttons"
      :key="`button-${buttonIndex}`"
    >
      <!--
        Postback button: no URL, disabled, or popup window enabled
        These don't need an anchor wrapper
      -->
      <el-button
        v-if="
          !((button.type && button.type === 'website') || button.url) ||
          isButtonDisabled(button) ||
          button.popupWindow?.enabled
        "
        plain
        v-html="button.text"
        class="message-bubble-button"
        :class="{ 'blue-button': buttonOutline === 'primary' }"
        :type="buttonOutline"
        :disabled="isButtonDisabled(button)"
        @click="onClick($event, button)"
      ></el-button>
      <!--
        URL button: wraps button in anchor for native link behavior
        Opens in new tab by default, or specified target
      -->
      <a v-else :href="validUrl(button.url)" :target="button.popup ?? '_blank'">
        <el-button
          plain
          v-html="button.text"
          class="message-bubble-button"
          :class="{ 'blue-button': buttonOutline === 'primary' }"
          :type="buttonOutline"
          @click="onClick($event, button)"
        ></el-button>
      </a>
    </div>
  </div>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/Buttons
 * @description Action buttons for message interactions (postback, URL, upload)
 */
import { computed } from "vue";
import { useMainStore } from "@/stores/main";
import validUrl from "@/helpers/validUrl";
import * as _ from "lodash-es";

/**
 * Component props with type definitions
 */
const props = defineProps({
  /**
   * The message object containing button data
   * @type {Object}
   */
  message: {
    type: Object,
    required: true,
  },
  /**
   * Button outline style type (e.g., 'primary')
   * @type {String}
   */
  buttonOutline: {
    type: String,
    default: "",
  },
  /**
   * Whether buttons should be disabled during live chat
   * @type {Boolean}
   */
  shouldDisableIfInLivechat: {
    type: Boolean,
    default: false,
  },
});

/**
 * Component events
 */
const emit = defineEmits([
  /**
   * Emitted when a button is clicked for postback processing
   * @param {Object} button - Button data with $event attached
   */
  "sendPostback",
  /**
   * Emitted when an upload button is clicked
   * @param {Object} button - Button configuration for upload
   */
  "upload",
]);

/**
 * Pinia store instance
 */
const store = useMainStore();

/**
 * Access webchat settings from Pinia store
 * @returns {Object} The settings object
 */
const settings = computed(() => store.settings);

/**
 * Access messages from Pinia store
 * @returns {Array} Array of messages
 */
const messages = computed(() => store.messages);

/**
 * Access isSendingPostback state from Pinia store
 * @returns {Boolean} Whether a postback is currently being sent
 */
const isSendingPostback = computed(() => store.isSendingPostback);

/**
 * Button display mode constants
 */
const ButtonDisplayMode = {
  DEFAULT: "default",
  ALWAYS: "always",
  DISABLE_IF_NOT_LAST: "disableIfNotLast",
};

/**
 * Check if button should be disabled
 *
 * Evaluates multiple conditions:
 * - Button reuse settings
 * - Live chat handover triggers
 * - Button display mode
 * - Whether this is the last message
 *
 * @param {Object} button - Button object to check
 * @returns {boolean} True if button should be disabled
 */
function isButtonDisabled(button) {
  const disableButtonReuse = _.get(settings.value, "disableButtonReuse");
  const replyMessages = messages.value.filter(
    (message) => message.type === "reply" && !message?.options?.excludeFromReplyMessages
  );

  // Check button display mode
  const buttonDisplayMode = _.get(
    props.message,
    "options.buttonDisplayMode",
    ButtonDisplayMode.DEFAULT
  );
  const disableButtonIfNotLast =
    buttonDisplayMode === ButtonDisplayMode.DISABLE_IF_NOT_LAST ||
    (buttonDisplayMode === ButtonDisplayMode.DEFAULT && !settings.value?.alwaysEnableButton);

  const isLastMessage = props.message.id === _.last(replyMessages)?.id;
  if (isLastMessage) {
    return false;
  }

  // Match any livechat start trigger
  const isHandoverLivechatStart = JSON.stringify(button).match(
    /(handover_livechat_start|handover_router)/
  );

  // Match any livechat stop trigger
  const isHandoverLivechatStop = JSON.stringify(button).match(/handover_stop/);

  const isDisabled =
    isSendingPostback.value ||
    (disableButtonReuse && !!isHandoverLivechatStart) ||
    (props.shouldDisableIfInLivechat && !isHandoverLivechatStop) ||
    disableButtonIfNotLast;

  return isDisabled;
}

/**
 * Handle button click
 *
 * Routes to appropriate handler based on button type:
 * 1. Upload buttons: Emit 'upload' event for parent to handle
 * 2. Popup buttons: Open URL in sized popup window
 * 3. All others: Emit sendPostback event with button data
 *
 * @param {Event} $event - DOM click event
 * @param {Object} button - Button configuration object
 */
function onClick($event, button) {
  // Check if this is an upload trigger button (by text content)
  const isUpload =
    button.text.toLowerCase().indexOf("upload") > -1 &&
    button.text.toLowerCase() !== "upload documents";

  if (isUpload) {
    // Emit upload event for parent component to handle
    // Parent (Message/Index.vue) should route this to FileUploader
    emit("upload", button);
  } else {
    // Handle popup window with custom dimensions
    if (button.url && button.popupWindow?.enabled) {
      $event.preventDefault();
      const width = button.popupWindow.width || 500;
      const height = button.popupWindow.height || 600;
      const windowFeatures = `width=${width},height=${height}`;
      window.open(validUrl(button.url), "_blank", windowFeatures);
    }
    // Emit postback event with button data and original event
    const _button = {
      ...button,
      $event,
    };
    emit("sendPostback", _button);
  }
}
</script>

<style lang="scss" scoped>
.message-bubble-button {
  font-family: var(--keyreply-font-family);
}

.ui-beta {
  .message-bubble-button {
    // width: auto !important;
    word-wrap: break-word;
    font-size: 12px !important;
    border-radius: 8px;
    line-height: 16px;
    margin: 0 0 10px 0;
    white-space: normal;
    width: 100%;

    &.blue-button {
      border: 1px solid #4e6cce;
      color: #4e6cce;
      background: #fff;

      &:hover {
        color: #fff;
        background: #4e6cce;
      }
    }
  }
}
</style>
